import { useCallback } from 'react'
import useModelsStore from '../stores/models'
import usePrefStore from '../stores/prefs'
import glob_const from '../libs/glob_const'
import type { Model, RawModel, GammeData, SKUParts } from '../types'

/** Entrée de liste traitée (label résolu dans la langue courante) */
interface ListItem {
   id: string
   label: string
   order?: number
   [key: string]: unknown
}

type SortField = 'id' | 'label' | 'design' | 'argu'

const useCollections = () => {
   const { collections, sous_collections, univers, familles, portes, typologies, cycles, etapes, genres, couleurs, models } = useModelsStore()
   const { currentLang: curLang, defaultLang: defLang } = usePrefStore()

   const HJO = 'HJO'
   const FJO = 'FJO'

   /**
    * Construit un SKU à partir du model, couleur, taille avec couleur et taille qui peuvent être null
    */
   const makeSKU = useCallback((model: string, color: string | null, size: string | null): string => {
      // console.log('makeSKU', model, color, size, [model, color, size].filter((item) => item && item !== '-').join('-'))
      return [model, color, size].filter((item) => item && item !== '-').join('-')
   }, [])

   /**
    * Déstructure un SKU en model, couleur, taille avec couleur et taille qui peuvent être null
    */
   const splitSKU = useCallback((sku: string | null | undefined): SKUParts | null => {
      const parts = sku?.split('-')

      // console.log('>>', sku, parts)
      if (parts) {
         return { model: parts[0], color: parts[1] ?? null, size: parts[2] ?? null }
      } else {
         return null
      }
   }, [])

   const gammeSort = useCallback((a: ListItem, b: ListItem) => {
      const specialGammes: Record<string, string> = {
         JOI: ' A',
         FJO: ' B',
         HJO: ' C',
         ACC: ' D'
      }
      let valA, valB

      valA = specialGammes[a.id] ?? String(a.id)
      valB = specialGammes[b.id] ?? String(b.id)
      return valA.localeCompare(valB)
   }, [])

   const sortOnLabel = useCallback((a: ListItem, b: ListItem) => {
      return a.label.localeCompare(b.label)
   }, [])

   const sortOnIdString = useCallback((a: ListItem, b: ListItem) => {
      return a.id.localeCompare(b.id)
   }, [])

   /**
    * Extrait un champ de texte dans la langue demandée
    * obj = l'objet
    * field = le nom du champ de cet objet qui contient un objet {fr:"...", en:"..."}
    */
   const getTextInLang = useCallback(
      (obj: Record<string, unknown>, field: string, lang: string | null): string => {
         const obj_field = obj[field]
         let text: string

         if (!obj_field) {
            text = '?1'
         } else {
            if (typeof obj_field === 'object') {
               if (!lang) {
                  text = (obj_field as Record<string, string>)[curLang] || '?2'
               } else {
                  text = (obj_field as Record<string, string>)[lang] || (obj_field as Record<string, string>)[defLang] || '?3'
               }
            } else {
               text = '?4'
            }
         }
         return text
      },
      [curLang, defLang]
   )

   /**
    * Exemple de retour (label dans la langue par défaut) :
    * {
    *    "HJO": { id: "HJO", order: 1, label: "Haute-Joaillerie" },
    *    ...
    * }
    */
   const getGammes = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(collections).reduce<Record<string, ListItem>>((prev, id) => {
            const a_gamme = collections[id] as GammeData & Record<string, unknown>
            const label = getTextInLang(a_gamme, 'label', lang)

            return {
               ...prev,
               [id]: { id, order: a_gamme.order, label }
            }
         }, {})
      },
      [collections, getTextInLang]
   )

   const getGammesAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getGammes(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getGammes]
   )

   const getGammesAsSortedArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         return getGammesAsArray(lang).sort(gammeSort)
      },
      [getGammesAsArray, gammeSort]
   )

   const getGammeById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const arr = getGammesAsArray(lang).filter((gamme) => gamme.id === id)
         if (arr.length === 1) {
            return { ...arr[0] }
         } else {
            console.error(`getGammeById(${id}) - Pas trouvé !`)
            return { id, label: '' }
         }
      },
      [getGammesAsArray]
   )

   /**
    * Retourne toutes les collections.
    */
   const getCollections = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         const gammes = getGammes()

         const colls = Object.keys(gammes).reduce<Record<string, unknown>>((prev, key) => {
            return { ...prev, ...(collections[key] as GammeData).collections }
         }, {})

         return Object.keys(colls).reduce<Record<string, ListItem>>((prev, id) => {
            const a_coll = colls[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_coll, 'label', lang)

            return { ...prev, [id]: { id, label } }
         }, {})
      },
      [collections, getGammes, getTextInLang]
   )

   const getCollectionsAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getCollections(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getCollections]
   )

   const getCollectionsAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getCollectionsAsArray(lang).sort(cmpFunc)
      },
      [getCollectionsAsArray, sortOnIdString, sortOnLabel]
   )

   const getCollectionsInGammes = useCallback(
      (gammes: string | string[], lang: string | null = null): Record<string, ListItem> => {
         let arr_gammes: string[]

         if (Array.isArray(gammes)) {
            arr_gammes = gammes
         } else {
            arr_gammes = [gammes]
         }

         const ret = arr_gammes.reduce<Record<string, ListItem>>((prev, gamme) => {
            if (typeof collections[gamme] !== 'object') {
               console.error(`getCollectionsInGammes(${gamme}, ${lang}) - Pas un objet !`)
               return { ...prev }
            }
            const colls = (collections[gamme] as GammeData).collections
            if (typeof colls !== 'object' || !colls) {
               console.error(`getCollectionsInGammes(${gamme}, ${lang}) - collections: Pas un objet !`)
               return { ...prev }
            }
            return {
               ...prev,
               ...Object.keys(colls).reduce<Record<string, ListItem>>((prev, id) => {
                  const a_coll = colls[id] as unknown as Record<string, unknown>
                  const label = getTextInLang(a_coll, 'label', lang)

                  return {
                     ...prev,
                     [id]: { id, label }
                  }
               }, {})
            }
         }, {})
         return ret
      },
      [collections, getTextInLang]
   )

   const getGammeForCollectionId = useCallback(
      (id: string, lang: string | null = null): ListItem | undefined => {
         return getGammesAsArray().find((gamme) => {
            return getCollectionsInGammes(gamme.id)[id] !== undefined
         })
      },
      [getCollectionsInGammes, getGammesAsArray]
   )

   const getCollectionsInGammesAsArray = useCallback(
      (gammes: string | string[], lang: string | null = null): ListItem[] => {
         const obj = getCollectionsInGammes(gammes, lang)
         return Object.keys(obj).map((id) => {
            return { id, ...obj[id] }
         })
      },
      [getCollectionsInGammes]
   )

   const getCollectionsInGammesAsSortedArray = useCallback(
      (gammes: string | string[], field: SortField = 'id', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getCollectionsInGammesAsArray(gammes, lang).sort(cmpFunc)
      },
      [getCollectionsInGammesAsArray, sortOnIdString, sortOnLabel]
   )

   const getCollectionById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const all = getCollectionsInGammesAsArray(Object.keys(getGammes()))
         const arr = all.filter((collection) => collection.id === id)
         if (arr.length === 1) {
            return { ...arr[0] }
         } else {
            console.error(`getCollectionById(${id}) - Pas trouvé !`)
            return { id, label: '' }
         }
      },
      [getCollectionsInGammesAsArray, getGammes]
   )

   const destructModel = useCallback(
      (model: RawModel, id: string, lang: string | null = null): Model => {
         const desig = getTextInLang(model as unknown as Record<string, unknown>, 'desig', lang)
         const label = getTextInLang(model as unknown as Record<string, unknown>, 'label', lang)
         const argu = getTextInLang(model as unknown as Record<string, unknown>, 'argu', lang)

         return { ...model, id, desig, label, argu } as Model
      },
      [getTextInLang]
   )

   /**
    * Exemple de retour (label dans la langue par défaut) :
    * { "04186": { id: "04186", label: "Gatsby Meli Melo large R", ... }, ... }
    */
   const getAllModels = useCallback(
      (lang: string | null = null): Record<string, Model> => {
         return Object.keys(models).reduce<Record<string, Model>>((prev, id) => {
            const a_model = models[id]

            return { ...prev, [id]: destructModel(a_model, id, lang) }
         }, {})
      },
      [models, destructModel]
   )

   const getAllModelsAsArray = useCallback(
      (lang: string | null = null): Model[] => {
         const obj = getAllModels(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getAllModels]
   )

   const getAllModelsAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): Model[] => {
         let cmpFunc: (a: Model, b: Model) => number

         if (field === 'id') {
            cmpFunc = (a, b) => a.id.localeCompare(b.id)
         } else if (field === 'design') {
            cmpFunc = (a, b) => (a.desig ?? '').localeCompare(b.desig ?? '')
         } else if (field === 'argu') {
            cmpFunc = (a, b) => (a.argu ?? '').localeCompare(b.argu ?? '')
         } else {
            cmpFunc = (a, b) => a.label.localeCompare(b.label)
         }

         return getAllModelsAsArray(lang).sort(cmpFunc)
      },
      [getAllModelsAsArray]
   )

   const getModelsInCollection = useCallback(
      (collection: string, lang: string | null = null): Record<string, Model> => {
         return Object.keys(models).reduce<Record<string, Model>>((prev, id) => {
            const a_model = models[id]

            if (a_model.collec === collection) {
               return { ...prev, [id]: destructModel(a_model, id, lang) }
            } else {
               return prev
            }
         }, {})
      },
      [models, destructModel]
   )

   const getModelsInCollectionAsArray = useCallback(
      (collection: string, lang: string | null = null): Model[] => {
         const obj = getModelsInCollection(collection, lang)
         return Object.keys(obj).map((id) => {
            return { ...obj[id] }
         })
      },
      [getModelsInCollection]
   )

   const getModelsInCollectionAsSortedArray = useCallback(
      (collection: string, field: SortField = 'id', lang: string | null = null): Model[] => {
         let cmpFunc: (a: Model, b: Model) => number

         if (field === 'id') {
            cmpFunc = (a, b) => a.id.localeCompare(b.id)
         } else {
            cmpFunc = (a, b) => a.label.localeCompare(b.label)
         }
         return getModelsInCollectionAsArray(collection, lang).sort(cmpFunc)
      },
      [getModelsInCollectionAsArray]
   )

   const getModelById = useCallback(
      (id: string, lang: string | null = null): Model | null => {
         const a_model = models[id]
         if (a_model) {
            return destructModel(a_model, id, lang)
         } else {
            return null
         }
      },
      [destructModel, models]
   )

   const isHJOModel = useCallback(
      (id: string): boolean => {
         const a_model = models[id]
         return a_model?.gamme === HJO
      },
      [models]
   )

   const isFJOModel = useCallback(
      (id: string): boolean => {
         const a_model = models[id]
         return a_model?.gamme === FJO
      },
      [models]
   )

   const isExcluBoutique = useCallback((a_model: RawModel | null | undefined): boolean => {
      if (!a_model) {
         return false
      } else {
         return a_model.reseau_ret === true && !a_model.reseau_ere && !a_model.reseau_eco && !a_model.reseau_whs
      }
   }, [])

   const isBOMono = useCallback((a_model: RawModel | null | undefined): boolean => {
      if (!a_model || a_model.fam !== glob_const.fam_bo) {
         return false
      } else {
         return a_model.type_bo === glob_const.bo_mono
      }
   }, [])

   const isBOPaire = useCallback((a_model: RawModel | null | undefined): boolean => {
      if (!a_model || a_model.fam !== glob_const.fam_bo) {
         return false
      } else {
         return a_model.type_bo === glob_const.bo_paire
      }
   }, [])

   const getFamilles = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(familles).reduce<Record<string, ListItem>>((prev, id) => {
            const a_famille = familles[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_famille, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [familles, getTextInLang]
   )

   const getFamillesAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getFamilles(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getFamilles]
   )

   const getFamillesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getFamillesAsArray(lang).sort(cmpFunc)
      },
      [getFamillesAsArray, sortOnIdString, sortOnLabel]
   )

   const getFamilleById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_famille = familles[id] as unknown as Record<string, unknown> | undefined
         if (a_famille) {
            const label = getTextInLang(a_famille, 'label', lang)

            return { ...a_famille, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [familles, getTextInLang]
   )

   const getPortes = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(portes).reduce<Record<string, ListItem>>((prev, id) => {
            const a_porte = portes[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_porte, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [portes, getTextInLang]
   )

   const getPortesAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getPortes(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getPortes]
   )

   const getPortesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getPortesAsArray(lang).sort(cmpFunc)
      },
      [getPortesAsArray, sortOnIdString, sortOnLabel]
   )

   const getPorteById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_porte = portes[id] as unknown as Record<string, unknown> | undefined
         if (a_porte) {
            const label = getTextInLang(a_porte, 'label', lang)

            return { ...a_porte, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [portes, getTextInLang]
   )

   const getTypologies = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(typologies).reduce<Record<string, ListItem>>((prev, id) => {
            const a_typo = typologies[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_typo, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [typologies, getTextInLang]
   )

   const getTypologiesAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getTypologies(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getTypologies]
   )

   const getTypologiesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getTypologiesAsArray(lang).sort(cmpFunc)
      },
      [getTypologiesAsArray, sortOnIdString, sortOnLabel]
   )

   const getTypologieById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_typo = typologies[id] as unknown as Record<string, unknown> | undefined
         if (a_typo) {
            const label = getTextInLang(a_typo, 'label', lang)

            return { ...a_typo, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [typologies, getTextInLang]
   )

   const getSousCollections = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(sous_collections).reduce<Record<string, ListItem>>((prev, id) => {
            const a_coll = sous_collections[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_coll, 'label', 'fr')

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [sous_collections, getTextInLang]
   )

   const getSousCollectionsAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getSousCollections(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getSousCollections]
   )

   const getSousCollectionsAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getSousCollectionsAsArray(lang).sort(cmpFunc)
      },
      [getSousCollectionsAsArray, sortOnIdString, sortOnLabel]
   )

   const getSousCollectionById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_coll = sous_collections[id] as unknown as Record<string, unknown> | undefined
         if (a_coll) {
            const label = getTextInLang(a_coll, 'label', 'fr')

            return { ...a_coll, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [sous_collections, getTextInLang]
   )

   const getUnivers = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(univers).reduce<Record<string, ListItem>>((prev, id) => {
            const a_univers = univers[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_univers, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [univers, getTextInLang]
   )

   const getUniversAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getUnivers(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getUnivers]
   )

   const getUniversAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getUniversAsArray(lang).sort(cmpFunc)
      },
      [getUniversAsArray, sortOnIdString, sortOnLabel]
   )

   const getUniversById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_univers = univers[id] as unknown as Record<string, unknown> | undefined
         if (a_univers) {
            const label = getTextInLang(a_univers, 'label', lang)

            return { ...a_univers, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [univers, getTextInLang]
   )

   const getCycles = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(cycles).reduce<Record<string, ListItem>>((prev, id) => {
            const a_cycle = cycles[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_cycle, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [cycles, getTextInLang]
   )

   const getCyclesAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getCycles(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getCycles]
   )

   const getCyclesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getCyclesAsArray(lang).sort(cmpFunc)
      },
      [getCyclesAsArray, sortOnIdString, sortOnLabel]
   )

   const getCycleById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_cycle = cycles[id] as unknown as Record<string, unknown> | undefined
         if (a_cycle) {
            const label = getTextInLang(a_cycle, 'label', lang)

            return { ...a_cycle, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [cycles, getTextInLang]
   )

   const getEtapes = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(etapes).reduce<Record<string, ListItem>>((prev, id) => {
            const a_etape = etapes[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_etape, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [etapes, getTextInLang]
   )

   const getEtapesAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getEtapes(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getEtapes]
   )

   const getEtapesAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getEtapesAsArray(lang).sort(cmpFunc)
      },
      [getEtapesAsArray, sortOnIdString, sortOnLabel]
   )

   const getEtapeById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_etape = etapes[id] as unknown as Record<string, unknown> | undefined
         if (a_etape) {
            const label = getTextInLang(a_etape, 'label', lang)

            return { ...a_etape, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [etapes, getTextInLang]
   )

   const getGenres = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(genres).reduce<Record<string, ListItem>>((prev, id) => {
            const a_genre = genres[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_genre, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [genres, getTextInLang]
   )

   const getGenresAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getGenres(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getGenres]
   )

   const getGenresAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getGenresAsArray(lang).sort(cmpFunc)
      },
      [getGenresAsArray, sortOnIdString, sortOnLabel]
   )

   const getGenreById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_genre = genres[id] as unknown as Record<string, unknown> | undefined
         if (a_genre) {
            const label = getTextInLang(a_genre, 'label', lang)

            return { ...a_genre, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [genres, getTextInLang]
   )

   const getCouleurs = useCallback(
      (lang: string | null = null): Record<string, ListItem> => {
         return Object.keys(couleurs).reduce<Record<string, ListItem>>((prev, id) => {
            const a_couleur = couleurs[id] as unknown as Record<string, unknown>
            const label = getTextInLang(a_couleur, 'label', lang)

            return {
               ...prev,
               [id]: { id, label }
            }
         }, {})
      },
      [couleurs, getTextInLang]
   )

   const getCouleursAsArray = useCallback(
      (lang: string | null = null): ListItem[] => {
         const obj = getCouleurs(lang)
         return Object.keys(obj).map((id) => {
            return obj[id]
         })
      },
      [getCouleurs]
   )

   const getCouleursAsSortedArray = useCallback(
      (field: SortField = 'label', lang: string | null = null): ListItem[] => {
         let cmpFunc: (a: ListItem, b: ListItem) => number

         if (field === 'id') {
            cmpFunc = sortOnIdString
         } else {
            cmpFunc = sortOnLabel
         }
         return getCouleursAsArray(lang).sort(cmpFunc)
      },
      [getCouleursAsArray, sortOnIdString, sortOnLabel]
   )

   const getCouleurById = useCallback(
      (id: string, lang: string | null = null): ListItem => {
         const a_couleur = couleurs[id] as unknown as Record<string, unknown> | undefined
         if (a_couleur) {
            const label = getTextInLang(a_couleur, 'label', lang)

            return { ...a_couleur, id, label } as ListItem
         } else {
            return {} as ListItem
         }
      },
      [couleurs, getTextInLang]
   )

   return {
      makeSKU,
      splitSKU,
      gammeSort,
      getGammes,
      getGammesAsArray,
      getGammesAsSortedArray,
      getGammeById,
      getGammeForCollectionId,
      getCollections,
      getCollectionsAsArray,
      getCollectionsAsSortedArray,
      getCollectionsInGammes,
      getCollectionsInGammesAsArray,
      getCollectionsInGammesAsSortedArray,
      getSousCollections,
      getSousCollectionsAsArray,
      getSousCollectionsAsSortedArray,
      getAllModels,
      getAllModelsAsArray,
      getAllModelsAsSortedArray,
      getModelsInCollection,
      getModelsInCollectionAsArray,
      getModelsInCollectionAsSortedArray,
      getCollectionById,
      getSousCollectionById,
      getModelById,
      isHJOModel,
      isFJOModel,
      isExcluBoutique,
      isBOMono,
      isBOPaire,
      getFamilles,
      getFamillesAsArray,
      getFamillesAsSortedArray,
      getFamilleById,
      getPortes,
      getPortesAsArray,
      getPortesAsSortedArray,
      getPorteById,
      getTypologies,
      getTypologiesAsArray,
      getTypologiesAsSortedArray,
      getTypologieById,
      getUnivers,
      getUniversAsArray,
      getUniversAsSortedArray,
      getUniversById,
      getCycles,
      getCyclesAsArray,
      getCyclesAsSortedArray,
      getCycleById,
      getEtapes,
      getEtapesAsArray,
      getEtapesAsSortedArray,
      getEtapeById,
      getGenres,
      getGenresAsArray,
      getGenresAsSortedArray,
      getGenreById,
      getCouleurs,
      getCouleursAsArray,
      getCouleursAsSortedArray,
      getCouleurById
   }
}

export default useCollections
export type { ListItem, SortField }
